import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  AlertOctagon, Clock, User, CheckCircle, AlertTriangle, 
  ChevronRight, Filter, RefreshCw, ExternalLink, Shield,
  ArrowUp, Timer, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import api from '../services/api';
import { toast } from 'sonner';

const IncidentCenter = () => {
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newIncident, setNewIncident] = useState({
    title: '',
    description: '',
    severity: 'medium',
    incident_type: 'policy_violation'
  });

  useEffect(() => {
    fetchIncidents();
    fetchStats();
  }, [statusFilter, severityFilter]);

  const fetchIncidents = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (severityFilter !== 'all') params.severity = severityFilter;
      const response = await api.get('/incidents', { params });
      setIncidents(response.data);
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/incidents/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleCreateIncident = async () => {
    try {
      await api.post('/incidents', newIncident);
      toast.success('Incident created');
      setShowCreateDialog(false);
      setNewIncident({ title: '', description: '', severity: 'medium', incident_type: 'policy_violation' });
      fetchIncidents();
      fetchStats();
    } catch (error) {
      toast.error('Failed to create incident');
    }
  };

  const handleTransition = async (incidentId, newStatus) => {
    try {
      await api.post(`/incidents/${incidentId}/transition?new_status=${newStatus}`);
      toast.success(`Status changed to ${newStatus}`);
      fetchIncidents();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleEscalate = async (incidentId) => {
    try {
      await api.post(`/incidents/${incidentId}/escalate?reason=Manual escalation from dashboard`);
      toast.success('Incident escalated');
      fetchIncidents();
    } catch (error) {
      toast.error('Failed to escalate');
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <AlertOctagon className="w-4 h-4 text-[#FF3B30]" />;
      case 'high': return <AlertTriangle className="w-4 h-4 text-[#FF6961]" />;
      case 'medium': return <Shield className="w-4 h-4 text-[#FFCC00]" />;
      default: return <Shield className="w-4 h-4 text-[#0066FF]" />;
    }
  };

  const getSeverityClass = (severity) => {
    switch (severity) {
      case 'critical': return 'risk-critical';
      case 'high': return 'risk-high';
      case 'medium': return 'risk-medium';
      default: return 'risk-low';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'new': return 'text-[#0066FF] bg-[#0066FF]/10';
      case 'triaged': return 'text-[#FFCC00] bg-[#FFCC00]/10';
      case 'investigating': return 'text-[#FF6961] bg-[#FF6961]/10';
      case 'remediating': return 'text-[#34C759] bg-[#34C759]/10';
      case 'resolved': return 'text-[#34C759] bg-[#34C759]/10';
      case 'escalated': return 'text-[#FF3B30] bg-[#FF3B30]/10';
      default: return 'text-[#9CA3AF] bg-[#9CA3AF]/10';
    }
  };

  return (
    <div data-testid="incident-center">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#F9FAFB] mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Incident Center
          </h1>
          <p className="text-[#9CA3AF]">
            Manage security incidents from detection to resolution
          </p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="primary-btn" data-testid="create-incident-btn">
              <AlertOctagon className="w-4 h-4 mr-2" />
              Create Incident
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-[#27272A]">
            <DialogHeader>
              <DialogTitle className="text-[#F9FAFB]">Create New Incident</DialogTitle>
              <DialogDescription className="text-[#9CA3AF]">
                Manually create a security incident
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#9CA3AF]">Title</Label>
                <Input
                  value={newIncident.title}
                  onChange={(e) => setNewIncident({ ...newIncident, title: e.target.value })}
                  className="bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none"
                  placeholder="Brief incident title"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#9CA3AF]">Description</Label>
                <Textarea
                  value={newIncident.description}
                  onChange={(e) => setNewIncident({ ...newIncident, description: e.target.value })}
                  className="bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none"
                  placeholder="Detailed description..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">Severity</Label>
                  <Select 
                    value={newIncident.severity} 
                    onValueChange={(v) => setNewIncident({ ...newIncident, severity: v })}
                  >
                    <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">Type</Label>
                  <Select 
                    value={newIncident.incident_type} 
                    onValueChange={(v) => setNewIncident({ ...newIncident, incident_type: v })}
                  >
                    <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                      <SelectItem value="policy_violation">Policy Violation</SelectItem>
                      <SelectItem value="unauthorized_access">Unauthorized Access</SelectItem>
                      <SelectItem value="anomaly_detected">Anomaly Detected</SelectItem>
                      <SelectItem value="credential_compromise">Credential Compromise</SelectItem>
                      <SelectItem value="privilege_escalation">Privilege Escalation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="secondary-btn">
                Cancel
              </Button>
              <Button onClick={handleCreateIncident} className="primary-btn">
                Create Incident
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="enterprise-card p-4">
            <div className="stat-value text-[#F9FAFB]">{stats.total}</div>
            <div className="stat-label">Total</div>
          </div>
          <div className="enterprise-card p-4">
            <div className="stat-value text-[#FF3B30]">{stats.by_severity.critical || 0}</div>
            <div className="stat-label">Critical</div>
          </div>
          <div className="enterprise-card p-4">
            <div className="stat-value text-[#FF6961]">{stats.by_severity.high || 0}</div>
            <div className="stat-label">High</div>
          </div>
          <div className="enterprise-card p-4">
            <div className="stat-value text-[#0066FF]">{stats.open_incidents}</div>
            <div className="stat-label">Open</div>
          </div>
          <div className="enterprise-card p-4">
            <div className="stat-value text-[#34C759]">
              {stats.mttr_minutes ? `${stats.mttr_minutes}m` : 'N/A'}
            </div>
            <div className="stat-label">Avg MTTR</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="enterprise-card p-4 mb-6">
        <div className="flex items-center gap-4">
          <Filter className="w-5 h-5 text-[#6B7280]" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="triaged">Triaged</SelectItem>
              <SelectItem value="investigating">Investigating</SelectItem>
              <SelectItem value="remediating">Remediating</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="escalated">Escalated</SelectItem>
            </SelectContent>
          </Select>
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className="w-[160px] bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none">
              <SelectValue placeholder="Severity" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
              <SelectItem value="all">All Severity</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchIncidents} variant="outline" className="secondary-btn ml-auto">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Incident Queue */}
      {loading ? (
        <div className="enterprise-card p-8 text-center text-[#9CA3AF]">Loading incidents...</div>
      ) : incidents.length === 0 ? (
        <div className="enterprise-card p-12 text-center">
          <CheckCircle className="w-12 h-12 text-[#34C759] mx-auto mb-4" />
          <p className="text-[#9CA3AF]">No incidents found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {incidents.map((incident) => (
            <div 
              key={incident.id}
              className={`enterprise-card p-6 border-l-4 ${
                incident.severity === 'critical' ? 'border-[#FF3B30]' :
                incident.severity === 'high' ? 'border-[#FF6961]' :
                incident.severity === 'medium' ? 'border-[#FFCC00]' :
                'border-[#0066FF]'
              }`}
              data-testid={`incident-${incident.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getSeverityIcon(incident.severity)}
                    <span className={getSeverityClass(incident.severity)}>{incident.severity}</span>
                    <span className={`px-2 py-0.5 text-xs font-mono ${getStatusClass(incident.status)}`}>
                      {incident.status}
                    </span>
                    {incident.sla_status?.response_breached && (
                      <span className="px-2 py-0.5 text-xs font-mono text-[#FF3B30] bg-[#FF3B30]/10 flex items-center gap-1">
                        <Timer className="w-3 h-3" /> SLA Breached
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-[#F9FAFB] mb-1">{incident.title}</h3>
                  <p className="text-[#9CA3AF] text-sm mb-3">{incident.description}</p>
                  <div className="flex items-center gap-4 text-xs text-[#6B7280]">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(incident.created_at).toLocaleString()}
                    </span>
                    {incident.agent_id && (
                      <Link 
                        to={`/dashboard/agents/${incident.agent_id}`}
                        className="text-[#0066FF] hover:text-[#3385FF] flex items-center gap-1"
                      >
                        View Agent <ExternalLink className="w-3 h-3" />
                      </Link>
                    )}
                    {incident.assigned_to && (
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {incident.assigned_to}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 ml-4">
                  {incident.status === 'new' && (
                    <Button 
                      size="sm"
                      onClick={() => handleTransition(incident.id, 'triaged')}
                      className="primary-btn text-xs"
                    >
                      Triage
                    </Button>
                  )}
                  {incident.status === 'triaged' && (
                    <Button 
                      size="sm"
                      onClick={() => handleTransition(incident.id, 'investigating')}
                      className="primary-btn text-xs"
                    >
                      Investigate
                    </Button>
                  )}
                  {incident.status === 'investigating' && (
                    <Button 
                      size="sm"
                      onClick={() => handleTransition(incident.id, 'remediating')}
                      className="primary-btn text-xs"
                    >
                      Remediate
                    </Button>
                  )}
                  {incident.status === 'remediating' && (
                    <Button 
                      size="sm"
                      onClick={() => handleTransition(incident.id, 'resolved')}
                      className="text-[#34C759] border-[#34C759]/50 hover:bg-[#34C759]/10 text-xs"
                      variant="outline"
                    >
                      Resolve
                    </Button>
                  )}
                  {!['resolved', 'closed', 'escalated'].includes(incident.status) && (
                    <Button 
                      size="sm"
                      onClick={() => handleEscalate(incident.id)}
                      className="text-[#FF3B30] border-[#FF3B30]/50 hover:bg-[#FF3B30]/10 text-xs"
                      variant="outline"
                    >
                      <ArrowUp className="w-3 h-3 mr-1" />
                      Escalate
                    </Button>
                  )}
                </div>
              </div>
              
              {/* SLA Info */}
              {incident.sla_status && incident.status !== 'resolved' && incident.status !== 'closed' && (
                <div className="mt-4 pt-4 border-t border-[#27272A]/40 flex items-center gap-6 text-xs">
                  {incident.sla_status.response_remaining_minutes !== null && (
                    <span className={incident.sla_status.response_breached ? 'text-[#FF3B30]' : 'text-[#9CA3AF]'}>
                      Response: {incident.sla_status.response_remaining_minutes}m remaining
                    </span>
                  )}
                  {incident.sla_status.resolution_remaining_minutes !== null && (
                    <span className={incident.sla_status.resolution_breached ? 'text-[#FF3B30]' : 'text-[#9CA3AF]'}>
                      Resolution: {incident.sla_status.resolution_remaining_minutes}m remaining
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default IncidentCenter;
